import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  LineChartOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectMilestoneDelayParty,
  createProjectMilestone,
  deleteProjectMilestone,
  forecastProjectMilestone,
  getProjectMilestones,
  projectMilestoneDelayPartyNames,
  recordProjectMilestone,
  reopenProjectMilestone,
  rescheduleProjectMilestone,
  updateProjectMilestone,
} from '../../api/projectMilestones';
import type {
  ProjectMilestone,
  ProjectMilestoneDelayPartyValue,
  ProjectMilestoneTimeline,
} from '../../api/projectMilestones';
import { formatDate } from './projectDisplay';
import { BrandColors } from '../../theme/brand';

/** Tham số của thẻ đường mốc. */
interface ProjectMilestoneCardProperties {
  readonly projectId: string;

  /** Được xem đường mốc. */
  readonly canView: boolean;

  /** Được khai, sửa, dời lịch và xóa mốc. */
  readonly canManage: boolean;

  /** Được ghi nhận mốc đã đạt và mở lại mốc. */
  readonly canRecord: boolean;
}

/** Giá trị của biểu mẫu khai và sửa mốc. */
interface MilestoneFormValues {
  readonly name: string;
  readonly description: string | null;
  readonly plannedDate: Dayjs;
  readonly isContractual: boolean;
  readonly isPaymentTrigger: boolean;
  readonly paymentPercent: number | null;
}

/** Giá trị của biểu mẫu ba thao tác đổi ngày. */
interface DateFormValues {
  readonly date: Dayjs | null;
  readonly delayParty: ProjectMilestoneDelayPartyValue | null;
  readonly delayReason: string | null;
}

/** Thao tác đổi ngày đang mở. */
type DateAction = 'reschedule' | 'forecast' | 'record';

/** Nhãn của từng thao tác đổi ngày. */
const dateActionTitles: Readonly<Record<DateAction, string>> = {
  reschedule: 'Dời lịch mốc',
  forecast: 'Đặt ngày dự báo',
  record: 'Ghi nhận mốc đã đạt',
};

/** Màu của con số cần chú ý — đỏ cho việc đã hỏng, gold đậm cho việc sắp hỏng. */
const alarmStyle = { color: BrandColors.red } as const;

/** Màu của con số cảnh báo sớm. */
const warningStyle = { color: BrandColors.goldDeep } as const;

/** Kiểu bình thường của con số: không tô màu. */
const normalStyle = {} as const;

/** Đổi chuỗi ngày của máy chủ sang đối tượng ngày của giao diện. */
function toDayjs(value: string | null): Dayjs | null {
  return value === null ? null : dayjs(value.slice(0, 10));
}

/** Đổi đối tượng ngày sang chuỗi ngày mà máy chủ nhận. */
function toDateString(value: Dayjs): string {
  return value.format('YYYY-MM-DD');
}

/** Nhãn số ngày trượt: dương là muộn, âm là sớm. */
function slipTag(days: number): ReactNode {
  if (days === 0) {
    return <Tag color="default">Đúng hạn</Tag>;
  }

  return days > 0 ? (
    <Tag color="red">Trượt {days} ngày</Tag>
  ) : (
    <Tag color="green">Sớm {Math.abs(days)} ngày</Tag>
  );
}

/**
 * Thẻ đường mốc của dự án.
 *
 * <b>Hai cột ngày đứng cạnh nhau là điểm chính của cả màn hình:</b> ngày cam kết ban đầu và ngày kế
 * hoạch hiện hành. Một dự án đã dời lịch bốn lần sẽ hiện <i>đúng hạn so với kế hoạch</i> nhưng
 * <i>trượt sáu mươi ngày so với cam kết ban đầu</i> — và con số thứ hai mới là thứ khách hàng nhìn.
 *
 * <b>Bảng "ai gây trễ" nằm ngay đầu thẻ.</b> Nó trả lời câu hỏi quyết định khoản phạt chậm tiến độ:
 * trong tổng số ngày chậm, bao nhiêu ngày thuộc về phía mình và bao nhiêu thuộc về phía khách.
 */
export function ProjectMilestoneCard({
  projectId,
  canView,
  canManage,
  canRecord,
}: ProjectMilestoneCardProperties): ReactNode {
  const [milestoneForm] = Form.useForm<MilestoneFormValues>();
  const [dateForm] = Form.useForm<DateFormValues>();

  const [timeline, setTimeline] = useState<ProjectMilestoneTimeline | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [editing, setEditing] = useState<ProjectMilestone | null>(null);
  const [isMilestoneFormOpen, setIsMilestoneFormOpen] = useState(false);

  const [dateTarget, setDateTarget] = useState<ProjectMilestone | null>(null);
  const [dateAction, setDateAction] = useState<DateAction>('reschedule');

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!canView) {
      return;
    }

    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await getProjectMilestones(projectId);

        if (!isCurrent) {
          return;
        }

        setTimeline(loaded);
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được đường mốc của dự án.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [projectId, canView, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openCreate(): void {
    setEditing(null);
    milestoneForm.resetFields();
    milestoneForm.setFieldsValue({
      plannedDate: dayjs(),
      isContractual: true,
      isPaymentTrigger: false,
    });
    setIsMilestoneFormOpen(true);
  }

  function openEdit(milestone: ProjectMilestone): void {
    setEditing(milestone);
    milestoneForm.setFieldsValue({
      name: milestone.name,
      description: milestone.description,

      // Ngày ở đây chỉ để hiện; phép sửa không gửi nó lên vì đổi ngày là thao tác riêng.
      plannedDate: dayjs(milestone.plannedDate.slice(0, 10)),
      isContractual: milestone.isContractual,
      isPaymentTrigger: milestone.isPaymentTrigger,
      paymentPercent: milestone.paymentPercent,
    });
    setIsMilestoneFormOpen(true);
  }

  function openDateAction(milestone: ProjectMilestone, action: DateAction): void {
    setDateTarget(milestone);
    setDateAction(action);
    dateForm.resetFields();
    dateForm.setFieldsValue({
      date:
        action === 'forecast'
          ? toDayjs(milestone.forecastDate)
          : dayjs(milestone.plannedDate.slice(0, 10)),
      delayParty: milestone.delayParty,
      delayReason: milestone.delayReason,
    });
  }

  async function submitMilestone(values: MilestoneFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const payload = {
        sequenceNumber: editing?.sequenceNumber ?? 0,
        name: values.name,
        description: values.description ?? null,
        plannedDate: toDateString(values.plannedDate),
        isContractual: values.isContractual,
        isPaymentTrigger: values.isPaymentTrigger,
        paymentPercent: values.isPaymentTrigger ? (values.paymentPercent ?? null) : null,
        projectTaskId: editing?.projectTaskId ?? null,
      };

      if (editing === null) {
        await createProjectMilestone(projectId, payload);
        message.success('Đã khai mốc. Ngày vừa nhập cũng là ngày cam kết gốc của mốc này.');
      } else {
        await updateProjectMilestone(projectId, editing.id, payload);
        message.success('Đã lưu thay đổi.');
      }

      setIsMilestoneFormOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitDateAction(values: DateFormValues): Promise<void> {
    if (dateTarget === null) {
      return;
    }

    setIsSaving(true);

    try {
      const delayParty = values.delayParty ?? null;
      const delayReason = values.delayReason ?? null;

      if (dateAction === 'forecast') {
        await forecastProjectMilestone(projectId, dateTarget.id, {
          forecastDate: values.date === null ? null : toDateString(values.date),
          delayParty,
          delayReason,
        });

        message.success(
          values.date === null ? 'Đã bỏ ngày dự báo.' : 'Đã đặt ngày dự báo.',
        );
      } else if (values.date === null) {
        message.error('Chưa chọn ngày.');

        return;
      } else if (dateAction === 'reschedule') {
        await rescheduleProjectMilestone(projectId, dateTarget.id, {
          plannedDate: toDateString(values.date),
          delayParty,
          delayReason,
        });

        message.success('Đã dời lịch. Ngày cam kết gốc giữ nguyên để còn đối chiếu.');
      } else {
        await recordProjectMilestone(projectId, dateTarget.id, {
          actualDate: toDateString(values.date),
          delayParty,
          delayReason,
        });

        message.success('Đã ghi nhận mốc đạt.');
      }

      setDateTarget(null);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function reopen(milestone: ProjectMilestone): Promise<void> {
    try {
      await reopenProjectMilestone(projectId, milestone.id);

      message.success('Đã mở lại mốc. Lý do trễ đã khai vẫn được giữ.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không mở lại được.');
    }
  }

  async function remove(milestone: ProjectMilestone): Promise<void> {
    try {
      await deleteProjectMilestone(projectId, milestone.id);

      message.success('Đã xóa mốc.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  if (!canView) {
    return null;
  }

  const columns: TableColumnsType<ProjectMilestone> = [
    {
      title: 'Mốc',
      dataIndex: 'name',
      render: (name: string, milestone) => (
        <Space direction="vertical" size={0}>
          <Space size={4} wrap>
            <span>{name}</span>
            {milestone.isContractual ? <Tag color="purple">Hợp đồng</Tag> : null}
            {milestone.isPaymentTrigger ? (
              <Tag color="gold">Thanh toán {milestone.paymentPercent ?? 0}%</Tag>
            ) : null}
            {milestone.isOverdue ? <Tag color="red">Quá hạn</Tag> : null}
          </Space>
          {milestone.projectTaskName === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Việc: {milestone.projectTaskName}
            </Typography.Text>
          )}
          {milestone.delayReason === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {milestone.delayParty === null
                ? ''
                : `${projectMilestoneDelayPartyNames[milestone.delayParty]}: `}
              {milestone.delayReason}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Cam kết gốc',
      dataIndex: 'baselineDate',
      width: 120,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Kế hoạch',
      dataIndex: 'plannedDate',
      width: 120,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Dự báo',
      dataIndex: 'forecastDate',
      width: 120,
      render: (value: string | null) => formatDate(value),
    },
    {
      title: 'Thực tế',
      dataIndex: 'actualDate',
      width: 120,
      render: (value: string | null) => formatDate(value),
    },
    {
      title: 'So với gốc',
      dataIndex: 'slipDaysAgainstBaseline',
      width: 140,
      render: (days: number) => slipTag(days),
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      render: (_: unknown, milestone) => (
        <Space size={2}>
          <Tooltip title="Sửa khai báo">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={!canManage}
              onClick={() => {
                openEdit(milestone);
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              milestone.isReached
                ? 'Mốc đã đạt thì không dời lịch được nữa.'
                : 'Dời ngày kế hoạch — muộn hơn cam kết gốc thì phải khai lý do'
            }
          >
            <Button
              size="small"
              icon={<CalendarOutlined />}
              disabled={!canManage || milestone.isReached}
              onClick={() => {
                openDateAction(milestone, 'reschedule');
              }}
            />
          </Tooltip>
          <Tooltip
            title={
              milestone.isReached
                ? 'Mốc đã đạt nên không cần dự báo nữa.'
                : 'Đặt ngày dự báo — cảnh báo sớm, không đụng vào kế hoạch'
            }
          >
            <Button
              size="small"
              icon={<LineChartOutlined />}
              disabled={!canManage || milestone.isReached}
              onClick={() => {
                openDateAction(milestone, 'forecast');
              }}
            />
          </Tooltip>
          {milestone.isReached ? (
            <Tooltip title="Mở lại mốc — dùng khi ghi nhận nhầm">
              <Button
                size="small"
                icon={<UndoOutlined />}
                disabled={!canRecord}
                onClick={() => {
                  void reopen(milestone);
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Ghi nhận mốc đã đạt">
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={!canRecord}
                onClick={() => {
                  openDateAction(milestone, 'record');
                }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              milestone.isReached
                ? 'Mốc đã đạt là sự kiện đã xảy ra nên không xóa được.'
                : 'Xóa mốc'
            }
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!canManage || milestone.isReached}
              onClick={() => {
                void remove(milestone);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const isDateOpen = dateTarget !== null;

  /*
    Ô lý do chỉ hiện khi thao tác có thể làm mốc trễ. Hiện nó ở mọi trường hợp sẽ khiến người dùng
    gõ lý do cho một mốc đúng hạn, rồi nhận về một lỗi từ chối mà họ không hiểu vì sao.
  */
  const isPossiblyLate = dateTarget !== null;

  return (
    <Card
      size="small"
      title="Đường mốc dự án"
      extra={
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          disabled={!canManage}
          onClick={openCreate}
        >
          Khai mốc
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage === null ? null : <Alert type="error" showIcon message={errorMessage} />}

        {timeline === null ? null : (
          <>
            <Space size="large" wrap>
              <Statistic
                title="Đã đạt"
                value={timeline.reachedCount}
                suffix={`/ ${String(timeline.milestones.length)}`}
              />
              <Statistic
                title="Quá hạn"
                value={timeline.overdueCount}
                valueStyle={timeline.overdueCount > 0 ? alarmStyle : normalStyle}
              />
              <Statistic
                title="Đang dự báo trễ"
                value={timeline.atRiskCount}
                valueStyle={timeline.atRiskCount > 0 ? warningStyle : normalStyle}
              />
              <Statistic
                title="Trượt nhiều nhất so với gốc"
                value={timeline.worstSlipDaysAgainstBaseline}
                suffix="ngày"
                valueStyle={
                  timeline.worstSlipDaysAgainstBaseline > 0 ? alarmStyle : normalStyle
                }
              />
              <Statistic
                title="Đủ điều kiện xuất hóa đơn"
                value={timeline.paymentPercentReached}
                suffix={`% / ${String(timeline.paymentPercentTotal)}%`}
              />
            </Space>

            {timeline.delayDaysByParty.length === 0 ? null : (
              <Alert
                type="info"
                showIcon
                message="Số ngày trễ quy về từng bên"
                description={
                  <Space size={8} wrap>
                    {timeline.delayDaysByParty.map((entry) => (
                      <Tag key={entry.party} color={entry.party === 1 ? 'red' : 'blue'}>
                        {projectMilestoneDelayPartyNames[entry.party]}: {entry.days} ngày
                      </Tag>
                    ))}
                  </Space>
                }
              />
            )}
          </>
        )}

        <Table<ProjectMilestone>
          rowKey="id"
          size="small"
          loading={timeline === null && errorMessage === null}
          pagination={false}
          columns={columns}
          dataSource={timeline?.milestones ?? []}
          locale={{
            emptyText:
              'Chưa khai mốc nào. Mốc dự án là mốc hợp đồng — nghiệm thu tại xưởng, giao hàng, bàn giao — thứ khách hàng cũng nhìn vào.',
          }}
        />
      </Space>

      <Modal
        open={isMilestoneFormOpen}
        title={editing === null ? 'Khai mốc dự án' : `Sửa mốc ${editing.name}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setIsMilestoneFormOpen(false);
        }}
        onOk={() => {
          milestoneForm.submit();
        }}
      >
        <Form
          form={milestoneForm}
          layout="vertical"
          onFinish={(values) => void submitMilestone(values)}
        >
          <Form.Item
            name="name"
            label="Tên mốc"
            rules={[{ required: true, message: 'Nhập tên mốc.' }, { max: 200 }]}
          >
            <Input placeholder="Nghiệm thu tại xưởng" />
          </Form.Item>
          <Form.Item
            name="plannedDate"
            label="Ngày cam kết"
            tooltip={
              editing === null
                ? 'Ngày này đồng thời là NGÀY GỐC và không sửa được nữa. Dời lịch về sau là thao tác riêng.'
                : 'Không đổi được ở đây. Dùng nút dời lịch trên dòng của mốc.'
            }
            rules={[{ required: true, message: 'Chọn ngày cam kết.' }]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              disabled={editing !== null}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Căn cứ nghiệm thu"
            tooltip="Coi là đạt mốc khi nào — càng cụ thể càng ít tranh cãi lúc đối chiếu với khách."
            rules={[{ max: 2000 }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Chạy đủ 500 sản phẩm mẫu, tỷ lệ bỏ sót dưới 0,1%, có biên bản ký hai bên."
            />
          </Form.Item>
          <Space align="start" wrap size="middle" style={{ width: '100%' }}>
            <Form.Item name="isContractual" label="Mốc hợp đồng" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isPaymentTrigger" label="Gắn đợt thanh toán" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(before: MilestoneFormValues, after: MilestoneFormValues) =>
                before.isPaymentTrigger !== after.isPaymentTrigger
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('isPaymentTrigger') === true ? (
                  <Form.Item
                    name="paymentPercent"
                    label="Phần trăm giá trị hợp đồng"
                    rules={[{ required: true, message: 'Nhập phần trăm.' }]}
                  >
                    <InputNumber min={0.01} max={100} step={5} style={{ width: 200 }} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        open={isDateOpen}
        title={dateActionTitles[dateAction]}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => {
          setDateTarget(null);
        }}
        onOk={() => {
          dateForm.submit();
        }}
      >
        {dateTarget === null ? null : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={dateTarget.name}
              description={
                dateAction === 'reschedule'
                  ? `Cam kết gốc ${formatDate(dateTarget.baselineDate)}. Dời muộn hơn ngày này thì phải khai bên chịu trách nhiệm và lý do — đó là căn cứ khi bàn về khoản phạt chậm tiến độ.`
                  : dateAction === 'forecast'
                    ? `Kế hoạch ${formatDate(dateTarget.plannedDate)}. Dự báo không đụng vào kế hoạch: nó là lời cảnh báo sớm, còn kế hoạch là thứ đã cam kết.`
                    : `Kế hoạch ${formatDate(dateTarget.plannedDate)}. Đạt muộn hơn ngày này thì phải khai bên chịu trách nhiệm và lý do.`
              }
            />

            <Form
              form={dateForm}
              layout="vertical"
              onFinish={(values) => void submitDateAction(values)}
            >
              <Form.Item
                name="date"
                label={
                  dateAction === 'record'
                    ? 'Ngày thực tế đạt mốc'
                    : dateAction === 'forecast'
                      ? 'Ngày dự báo — để trống nghĩa là bỏ dự báo'
                      : 'Ngày kế hoạch mới'
                }
                rules={
                  dateAction === 'forecast'
                    ? []
                    : [{ required: true, message: 'Chọn ngày.' }]
                }
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  allowClear={dateAction === 'forecast'}
                />
              </Form.Item>

              {isPossiblyLate ? (
                <>
                  <Form.Item
                    name="delayParty"
                    label="Bên chịu trách nhiệm nếu trễ"
                    tooltip="Bắt buộc khi ngày mới muộn hơn mốc so sánh. Để trống nếu không trễ — khai khi không trễ sẽ bị từ chối."
                  >
                    <Select
                      allowClear
                      placeholder="Không trễ"
                      options={Object.values(ProjectMilestoneDelayParty).map((party) => ({
                        value: party,
                        label: projectMilestoneDelayPartyNames[party],
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="delayReason"
                    label="Lý do trễ"
                    tooltip="Càng cụ thể càng dùng được: dẫn ngày tháng, số biên bản, diễn biến."
                    rules={[{ max: 2000 }]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Mặt bằng bàn giao muộn 20 ngày so với biên bản ngày 3 tháng 4."
                    />
                  </Form.Item>
                </>
              ) : null}
            </Form>
          </Space>
        )}
      </Modal>
    </Card>
  );
}
